import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import Home from './components/Home';
import StaffDashboard from './components/StaffDashboard';
import CustomerLookup from './components/CustomerLookup';
import ContactUs from './components/ContactUs';
import Navigation from './components/Navigation';
import AdminLogin from './components/AdminLogin';
import Users from './components/Users';
import Updates from './components/Updates';
import CartoonTest from './components/CartoonTest';
import ClockPreview from './components/ClockPreview';
import Bills from './components/Bills';
import DataExport from './components/DataExport';
import Expenses from './components/Expenses';
import AppComingSoon from './components/AppComingSoon';
import Print from './components/Print';
import PrintJobs from './components/PrintJobs';
import Currencies from './components/Currencies';
import Inventory from './components/Inventory';
import Register from './components/Register';
import Shop from './components/Shop';
import Warranty from './components/Warranty';
import Finances from './components/Finances';
import AdminInbox from './components/AdminInbox';
import PrayerTimes from './components/PrayerTimes';
import Analytics from './components/Analytics';
import DemandList from './components/DemandList';
import Wallet from './components/Wallet';
import RewardsAdmin from './components/RewardsAdmin';
import { Toaster } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AppContent() {
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Anonymous page-view tracker — fires once per route change.
  // Backend rejects tracking of admin routes so numbers reflect real customer traffic.
  useEffect(() => {
    let sid = sessionStorage.getItem('ashop_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('ashop_sid', sid);
    }
    axios.post(`${API}/track`, { path: location.pathname || '/', session_id: sid }).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    // Check if admin is already logged in AND session hasn't expired (8 hours)
    const adminSession = localStorage.getItem('ashop_admin');
    const loginTime = parseInt(localStorage.getItem('ashop_admin_login_time') || '0');
    const EIGHT_HOURS = 8 * 60 * 60 * 1000;
    const sessionExpired = loginTime > 0 && (Date.now() - loginTime) > EIGHT_HOURS;

    if (sessionExpired) {
      // Clear expired session
      localStorage.removeItem('ashop_admin');
      localStorage.removeItem('ashop_admin_token');
      localStorage.removeItem('ashop_admin_login_time');
      setIsAdminLoggedIn(false);
      if (location.pathname.startsWith('/staff') || location.pathname === '/inventory' || location.pathname === '/register' || location.pathname === '/expenses' || location.pathname === '/bills' || location.pathname === '/warranty' || location.pathname === '/finances' || location.pathname === '/inbox' || location.pathname === '/prayer-times' || location.pathname === '/analytics' || location.pathname === '/currencies' || location.pathname === '/print-jobs' || location.pathname === '/export' || location.pathname === '/users' || location.pathname === '/updates') {
        navigate('/admin-login');
      }
      return;
    }
    if (adminSession === 'true') {
      setIsAdminLoggedIn(true);
      // If on admin-login page and already logged in, redirect to staff
      if (location.pathname === '/admin-login') {
        navigate('/staff');
      }
    } else {
      // If trying to access /staff without login, redirect to login
      if (location.pathname === '/staff') {
        navigate('/admin-login');
      }
    }
  }, [location.pathname, navigate]);

  const handleAdminLogin = (success) => {
    if (success) {
      setIsAdminLoggedIn(true);
      setShowAdminLogin(false);
      // Navigate to staff page after login
      setTimeout(() => {
        navigate('/staff');
      }, 100);
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('ashop_admin');
    localStorage.removeItem('ashop_admin_token');
    localStorage.removeItem('ashop_admin_login_time');
    setIsAdminLoggedIn(false);
    setShowAdminLogin(false);
    navigate('/');
  };

  // Hide navigation on home page, customer page, contact page, admin-login, app, print, and shop pages
  const showNavigation = location.pathname !== '/' && location.pathname !== '/customer' && location.pathname !== '/contact' && location.pathname !== '/admin-login' && location.pathname !== '/app' && location.pathname !== '/print' && location.pathname !== '/shop';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 relative overflow-hidden">
      {/* Background decoration - Rotterdam Maritime Theme */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full opacity-15 animate-bounce"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-gradient-to-r from-blue-300 to-orange-300 rounded-full opacity-25"></div>
        <div className="absolute bottom-40 right-1/3 w-24 h-24 bg-gradient-to-r from-blue-500 to-orange-400 rounded-full opacity-20 animate-pulse"></div>
      </div>
      
      {showNavigation && (
        <Navigation 
          isAdminLoggedIn={isAdminLoggedIn} 
          onAdminLogin={() => setShowAdminLogin(true)}
          onAdminLogout={handleAdminLogout}
        />
      )}
      <main className="relative z-10">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/customer" element={<div className="container mx-auto px-4 py-8"><CustomerLookup /></div>} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/app" element={<AppComingSoon />} />
          <Route path="/print" element={<Print />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/admin-login" element={<AdminLogin onLogin={handleAdminLogin} />} />
          <Route path="/admin" element={
            isAdminLoggedIn ? <Navigate to="/staff" replace /> : <Navigate to="/admin-login" replace />
          } />
          {isAdminLoggedIn ? (
            <>
              <Route path="/staff" element={<div className="w-full px-4 py-8 overflow-x-hidden"><StaffDashboard /></div>} />
              <Route path="/users" element={<Users />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/bills" element={<Bills />} />
              <Route path="/export" element={<DataExport />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/print-jobs" element={<PrintJobs />} />
              <Route path="/currencies" element={<Currencies />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/register" element={<Register />} />
              <Route path="/warranty" element={<Warranty />} />
              <Route path="/finances" element={<Finances />} />
              <Route path="/inbox" element={<AdminInbox />} />
              <Route path="/prayer-times" element={<PrayerTimes />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/demand-list" element={<DemandList />} />
              <Route path="/rewards-admin" element={<RewardsAdmin />} />
              <Route path="/wallet" element={<Wallet />} />
            </>
          ) : (
            <>
              <Route path="/staff" element={<Navigate to="/admin-login" replace />} />
              <Route path="/users" element={<Navigate to="/admin-login" replace />} />
              <Route path="/updates" element={<Navigate to="/admin-login" replace />} />
              <Route path="/bills" element={<Navigate to="/admin-login" replace />} />
              <Route path="/export" element={<Navigate to="/admin-login" replace />} />
              <Route path="/expenses" element={<Navigate to="/admin-login" replace />} />
              <Route path="/print-jobs" element={<Navigate to="/admin-login" replace />} />
              <Route path="/currencies" element={<Navigate to="/admin-login" replace />} />
              <Route path="/inventory" element={<Navigate to="/admin-login" replace />} />
              <Route path="/register" element={<Navigate to="/admin-login" replace />} />
              <Route path="/warranty" element={<Navigate to="/admin-login" replace />} />
              <Route path="/finances" element={<Navigate to="/admin-login" replace />} />
              <Route path="/inbox" element={<Navigate to="/admin-login" replace />} />
              <Route path="/prayer-times" element={<Navigate to="/admin-login" replace />} />
              <Route path="/analytics" element={<Navigate to="/admin-login" replace />} />
              <Route path="/demand-list" element={<Navigate to="/admin-login" replace />} />
              <Route path="/rewards-admin" element={<Navigate to="/admin-login" replace />} />
              <Route path="/wallet" element={<Wallet />} />
            </>
          )}
          <Route path="/cartoon-test" element={<CartoonTest />} />
          <Route path="/clock-preview" element={<ClockPreview />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;